import React, { useEffect, useState, useCallback } from 'react';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import * as expenseService from '../../services/expenseService';
import ExpenseTabs from '../../components/ExpenseTabs';
import { downloadBlob } from '../../utils/downloadFile';
import ScrollX from '../../components/ScrollX';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Approval roles mirror expense_routes.py's ADMIN_ROLES, broadened
// 2026-08-19 so HOD/Accounts_Manager/System_Admin/CEO can all approve
// factory bills. The backend additionally allows the assigned HOD of the
// Production/Operations department even without the literal 'HOD' role
// (require_factory_bill_approver) - that edge case isn't mirrored here, so
// the Approve button may not show for that one case even though the API
// would accept it; UI convenience only, real enforcement is server-side.
//
// Logging (deposits/bills) is open to anyone logged in (2026-08-19 user
// instruction: "everyone can log in all money, HOD/accounts/admin/CEO
// approve it") - mirrors create/update/delete_factory_deposit/bill now
// using get_current_employee instead of require_role.
const ADMIN_ROLES = ['HOD', 'Accounts_Manager', 'System_Admin', 'CEO'];

const TYPES = ['Electricity', 'Water', 'Rent', 'Internet', 'Maintenance', 'Insurance',
  'Transport / Travelling', 'Medical', 'Snacks', 'Indirect', 'Other'];

const APPROVAL_COLORS = {
  pending: 'bg-gray-200 text-gray-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const initialDepositForm = { type: 'Electricity', vendor_name: '', amount: '', deposit_date: '', notes: '' };
const initialBillForm = { type: 'Electricity', deposit_id: '', amount: '', bill_period: '', date_received: '', notes: '' };

const FactoryExpensesPage = () => {
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);

  const [deposits, setDeposits] = useState([]);
  const [bills, setBills] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [showBillForm, setShowBillForm] = useState(false);
  const [depositForm, setDepositForm] = useState(initialDepositForm);
  const [billForm, setBillForm] = useState(initialBillForm);
  const [isImporting, setIsImporting] = useState(false);

  // Filters, applied server-side so this scales as deposits/bills grow.
  const initialFilters = { type: '', approval_status: '', date_from: '', date_to: '' };
  const [filters, setFilters] = useState(initialFilters);
  const clearFilters = () => setFilters(initialFilters);

  const canLog = !!user;
  const canApprove = user && ADMIN_ROLES.includes(user.role);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const depositParams = {};
      if (filters.type) depositParams.type = filters.type;

      const billParams = { ...depositParams };
      if (filters.approval_status) billParams.approval_status = filters.approval_status;
      if (filters.date_from) billParams.date_from = filters.date_from;
      if (filters.date_to) billParams.date_to = filters.date_to;

      const [depRes, billRes, setRes] = await Promise.all([
        expenseService.getFactoryDeposits(depositParams),
        expenseService.getFactoryBills(billParams),
        expenseService.getFactorySettlements(),
      ]);
      setDeposits(depRes.data.deposits || []);
      setBills(billRes.data.bills || []);
      setSettlements(setRes.data.settlements || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load factory expenses', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleDepositSubmit = async (e) => {
    e.preventDefault();
    if (!depositForm.type || !depositForm.amount) {
      showToast('Type and amount are required', 'error');
      return;
    }
    try {
      await expenseService.createFactoryDeposit({
        type: depositForm.type,
        vendor_name: depositForm.vendor_name || null,
        amount: Number(depositForm.amount),
        deposit_date: depositForm.deposit_date || null,
        notes: depositForm.notes || null,
      });
      showToast('Deposit recorded', 'success');
      setDepositForm(initialDepositForm);
      setShowDepositForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to record deposit', 'error');
    }
  };

  const handleBillSubmit = async (e) => {
    e.preventDefault();
    if (!billForm.type || !billForm.amount || !billForm.date_received) {
      showToast('Type, amount, and date received are required', 'error');
      return;
    }
    try {
      await expenseService.createFactoryBill({
        type: billForm.type,
        deposit_id: billForm.deposit_id ? Number(billForm.deposit_id) : null,
        amount: Number(billForm.amount),
        bill_period: billForm.bill_period || null,
        date_received: billForm.date_received,
        notes: billForm.notes || null,
      });
      showToast('Bill logged', 'success');
      setBillForm(initialBillForm);
      setShowBillForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to log bill', 'error');
    }
  };

  // Excel template with a real dropdown for type, sourced from the DB -
  // see SiteExpensesPage.js's matching comment for why this isn't CSV.
  const handleDownloadBillTemplate = async () => {
    try {
      const res = await expenseService.getFactoryBillsImportTemplate();
      downloadBlob(res.data, 'factory_bills_template.xlsx');
    } catch (error) {
      showToast('Failed to download template', 'error');
    }
  };

  const handleImportBillsCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIsImporting(true);
    try {
      const res = await expenseService.importFactoryBills(file);
      const count = res.data.bills.length;
      showToast(`${count} bill${count !== 1 ? 's' : ''} imported`, 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to import file', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await expenseService.approveFactoryBill(id);
      showToast('Bill approved', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Approval failed', 'error');
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    try {
      await expenseService.rejectFactoryBill(id, reason);
      showToast('Bill rejected', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Rejection failed', 'error');
    }
  };

  const handleEditBill = async (bill) => {
    const amount = window.prompt('New amount (₹):', bill.amount);
    if (amount === null) return;
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      showToast('Enter a valid amount', 'error');
      return;
    }
    try {
      await expenseService.updateFactoryBill(bill.id, { amount: Number(amount) });
      showToast('Bill updated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update bill', 'error');
    }
  };

  const handleDeleteBill = async (bill) => {
    if (!window.confirm('Delete this bill? This cannot be undone.')) return;
    try {
      await expenseService.deleteFactoryBill(bill.id);
      showToast('Bill deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete bill', 'error');
    }
  };

  const handleMarkSettled = async (type) => {
    if (!window.confirm(`Mark all active ${type} deposits as balanced?`)) return;
    try {
      await expenseService.markFactorySettlementSettled(type);
      showToast('Marked balanced', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to mark balanced', 'error');
    }
  };

  const handleEditDeposit = async (deposit) => {
    const amount = window.prompt('New amount (₹):', deposit.amount);
    if (amount === null) return;
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      showToast('Enter a valid amount', 'error');
      return;
    }
    try {
      await expenseService.updateFactoryDeposit(deposit.id, { amount: Number(amount) });
      showToast('Deposit updated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update deposit', 'error');
    }
  };

  const handleDeleteDeposit = async (deposit) => {
    if (!window.confirm('Delete this deposit? This cannot be undone.')) return;
    try {
      await expenseService.deleteFactoryDeposit(deposit.id);
      showToast('Deposit deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete deposit', 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <ExpenseTabs />
        <h1 className="text-2xl font-bold text-white mb-6">Factory Expenses</h1>

        <div className="glass-card p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-3">Settlements by type</h2>
          {settlements.length === 0 ? (
            <p className="text-sm text-gray-400">No deposits or bills recorded yet.</p>
          ) : (
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium">Deposited</th>
                    <th className="px-3 py-2 font-medium">Billed</th>
                    <th className="px-3 py-2 font-medium">Balance</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s) => (
                    <tr key={s.type} className="border-b border-gray-50 last:border-0">
                      <td className="px-3 py-2">{s.type}</td>
                      <td className="px-3 py-2">{money(s.total_deposited)}</td>
                      <td className="px-3 py-2">{money(s.total_billed)}</td>
                      <td className="px-3 py-2 font-medium">{money(s.balance)}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {s.action === 'refund_due' ? 'Refund/claim due' : s.action === 'payment_due' ? 'Additional payment due' : 'Settled'}
                      </td>
                      <td className="px-3 py-2">
                        {s.action !== 'settled' && canApprove && (
                          <button onClick={() => handleMarkSettled(s.type)} className="text-green-700 hover:underline text-xs font-semibold">Mark Balanced</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>

        {/* Filters - applied server-side so this scales as deposits/bills grow */}
        <div className="glass-card p-4 mb-6 flex flex-wrap gap-2 items-center">
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All types</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filters.approval_status} onChange={(e) => setFilters({ ...filters, approval_status: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All statuses (bills)</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
          <span className="text-xs text-gray-400">to</span>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
          <button onClick={clearFilters} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 ml-auto">
            Clear Filters
          </button>
        </div>

        {/* Deposits Given */}
        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Deposits Given ({deposits.length})</h2>
          </div>
          {deposits.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No deposits recorded yet.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Vendor / Payee</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((d) => (
                  <tr key={d.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3">{d.type}</td>
                    <td className="px-4 py-3 text-gray-500">{d.vendor_name || '—'}</td>
                    <td className="px-4 py-3 font-medium">{money(d.amount)}</td>
                    <td className="px-4 py-3">{d.deposit_date}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${d.status === 'Active' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                      {d.status === 'Active' && canLog && (
                        <>
                          <button onClick={() => handleEditDeposit(d)} className="text-gray-600 hover:underline text-xs font-semibold">Edit</button>
                          <button onClick={() => handleDeleteDeposit(d)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>

        {canLog && (
          <div className="glass-card p-6 mb-6">
            <button onClick={() => setShowDepositForm(!showDepositForm)} className="text-sm font-semibold text-komal-blue hover:underline">
              {showDepositForm ? 'Hide' : '+ Record a deposit'}
            </button>
            {showDepositForm && (
              <form onSubmit={handleDepositSubmit} className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select value={depositForm.type} onChange={(e) => setDepositForm({ ...depositForm, type: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor / Payee</label>
                  <input value={depositForm.vendor_name} onChange={(e) => setDepositForm({ ...depositForm, vendor_name: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
                  <input required type="number" value={depositForm.amount} onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deposit date</label>
                  <input type="date" value={depositForm.deposit_date} onChange={(e) => setDepositForm({ ...depositForm, deposit_date: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Record Deposit</button>
                </div>
              </form>
            )}
          </div>
        )}

        {canLog && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <button onClick={() => setShowBillForm(!showBillForm)} className="text-sm font-semibold text-komal-blue hover:underline">
                {showBillForm ? 'Hide' : '+ Log a bill'}
              </button>
              <div className="flex items-center gap-2">
                <button onClick={handleDownloadBillTemplate} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                  Download Excel Template
                </button>
                <label className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer">
                  {isImporting ? 'Importing...' : 'Upload Excel'}
                  <input type="file" accept=".xlsx,.xlsm" onChange={handleImportBillsCsv} disabled={isImporting} className="hidden" />
                </label>
              </div>
            </div>
            {showBillForm && (
              <form onSubmit={handleBillSubmit} className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select value={billForm.type} onChange={(e) => setBillForm({ ...billForm, type: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Linked deposit</label>
                  <select value={billForm.deposit_id} onChange={(e) => setBillForm({ ...billForm, deposit_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">None</option>
                    {deposits.filter((d) => d.type === billForm.type).map((d) => (
                      <option key={d.id} value={d.id}>{money(d.amount)} on {d.deposit_date}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
                  <input required type="number" value={billForm.amount} onChange={(e) => setBillForm({ ...billForm, amount: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill period</label>
                  <input placeholder="e.g. Jan 2024" value={billForm.bill_period} onChange={(e) => setBillForm({ ...billForm, bill_period: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date received *</label>
                  <input required type="date" value={billForm.date_received} onChange={(e) => setBillForm({ ...billForm, date_received: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Log Bill</button>
                </div>
              </form>
            )}
          </div>
        )}

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Bills ({bills.length})</h2>
          </div>
          {isLoading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : bills.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No bills logged yet.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Period</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Received</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Approved By</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((b) => (
                  <tr key={b.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3">{b.type}</td>
                    <td className="px-4 py-3 text-gray-500">{b.bill_period || '—'}</td>
                    <td className="px-4 py-3 font-medium">{money(b.amount)}</td>
                    <td className="px-4 py-3">{b.date_received}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${APPROVAL_COLORS[b.approval_status]}`}>
                        {b.approval_status}
                      </span>
                      {b.approval_status === 'rejected' && b.rejection_reason && (
                        <div className="text-xs text-red-600 mt-1 max-w-[14rem]" title={b.rejection_reason}>{b.rejection_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{b.approved_by_name || '—'}</td>
                    <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                      {b.approval_status === 'pending' && canApprove && (
                        <>
                          <button onClick={() => handleApprove(b.id)} className="text-green-700 hover:underline text-xs font-semibold">Approve</button>
                          <button onClick={() => handleReject(b.id)} className="text-red-500 hover:underline text-xs font-semibold">Reject</button>
                        </>
                      )}
                      {b.approval_status === 'pending' && canLog && (
                        <>
                          <button onClick={() => handleEditBill(b)} className="text-gray-600 hover:underline text-xs font-semibold">Edit</button>
                          <button onClick={() => handleDeleteBill(b)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default FactoryExpensesPage;
